import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AppBar,
  IconButton,
  Modal,
  Toolbar,
  Typography,
  Button,
} from "@mui/material";
import { Box } from "@mui/system";
import { Close } from "@mui/icons-material";

// Custom hook to manage the dialog close behavior.
export const useCloseConfirmation = ({
  onDialogClose,
  setOpenDialog,
  isCloseWithOutConfirming = true, // Default is auto-close (no confirmation)
} = {}) => {
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  // This function closes the dialog.
  const handleDialogClose = () => {
    if (onDialogClose) {
      onDialogClose();
    } else if (typeof setOpenDialog === "function") {
      setOpenDialog(false);
    }
  };

  // When the close icon is clicked, decide what to do:
  // - If auto-close is enabled (default true), close immediately.
  // - Otherwise, open the confirmation modal.
  const handleCloseClick = () => {
    if (isCloseWithOutConfirming) {
      handleDialogClose();
    } else {
      setIsConfirmationModalOpen(true);
    }
  };

  // Called when the user confirms closing.
  const handleConfirmCloseDialog = () => {
    setIsConfirmationModalOpen(false);
    handleDialogClose();
  };

  // Called when the user cancels closing.
  const handleCancelCloseDialog = () => {
    setIsConfirmationModalOpen(false);
  };

  // App bar with the close icon.
  const DialogAppBar = () => (
    <AppBar
      position="absolute"
      color="default"
      elevation={0}
      sx={{ height: "40px", width: "100%", top: 0 }}
    >
      <Toolbar sx={{ justifyContent: "flex-end", height: "100%" }}>
        <IconButton
          onClick={handleCloseClick}
          aria-label="close"
          sx={{ color: "black", padding: "0", height: "40px" }}
        >
          <Close />
        </IconButton>
      </Toolbar>
    </AppBar>
  );

  // Confirmation modal is rendered only if auto-close is disabled.
  const CancelConfirmationModal = ({
    msg = "Are you sure you want to cancel?",
  }) => {
    if (isCloseWithOutConfirming) return null;
    return (
      <Modal open={isConfirmationModalOpen} onClose={handleCancelCloseDialog}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: "10px",
            outline: "none",
            maxWidth: "400px",
          }}
        >
          <Typography variant="h5" sx={{ mb: 2 }}>
            {msg}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button variant="contained" onClick={handleCancelCloseDialog} sx={{ mr: 2 }}>
              No
            </Button>
            <Button variant="outlined" onClick={handleConfirmCloseDialog}>
              Yes
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  };

  return {
    DialogAppBar,
    CancelConfirmationModal,
  };
};

// CustomDialog component that uses the hook.
const CustomDialog = (props) => {
  // Destructure props including optional isCloseWithOutConfirming.
  const {
    openDialog,
    setOpenDialog,
    onDialogClose,
    actions,
    maxWidth = "xs",
    fullWidth = true,
    isCloseWithOutConfirming, // Caller can pass this to override the default.
  } = props;

  const { DialogAppBar, CancelConfirmationModal } = useCloseConfirmation({
    onDialogClose,
    setOpenDialog,
    isCloseWithOutConfirming, // Pass along to the hook.
  });

  return (
    <>
      <Dialog open={openDialog} fullWidth={fullWidth} maxWidth={maxWidth}>
        <DialogTitle>
          <DialogAppBar />
        </DialogTitle>
        <DialogContent>{props.children}</DialogContent>
        {actions && <DialogActions>{actions}</DialogActions>}
      </Dialog>
      <CancelConfirmationModal />
    </>
  );
};

export default CustomDialog;
